import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { io, Socket } from "socket.io-client";
import configs from "../configs";
import { useRoot } from "../context/RootProvider";

interface SocketContextProps {
  socket: Socket | null;
  isConnected: boolean;
  notifications: any[];
  unreadNotifications: any;
}

const SocketContext = createContext<SocketContextProps | undefined>(undefined);

export const useSocket = (): SocketContextProps => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const { accesstoken } = useRoot();
  const [socketIsConnected, setSocketIsConnected] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const socket = useRef<Socket | null>(null);
  const [unreadNotifications, setUnread] = useState<{ unreadCount: number }>({
    unreadCount: 0,
  });
  const socketConfig = {
    auth: {
      token: `Bearer ${accesstoken}`,
    },
    transports: ["websocket"],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 3000,
    timeout: 10000,
    closeOnBeforeunload: true,
  };
  const addNotification = (newNotification: any) => {
    setNotifications((prevNotifications) => [
      newNotification,
      ...prevNotifications,
    ]);
  };

  useEffect(() => {
    if (notifications) {
      toast.success(notifications[0]?.title);
    }
  }, [notifications]);
  useEffect(() => {
    socket.current = io(configs.socketURL, socketConfig);

    socket.current.on("connect", () => {
      setSocketIsConnected(true);
      socket?.current?.emit("unread-notifications");
      socket?.current?.on("unread-notifications", (notifications) => {
        setUnread(notifications);
      });
    });

    socket.current.on("new-notification", (newNotification) => {
      addNotification(newNotification);
    });

    socket.current.on("disconnect", () => {
      setSocketIsConnected(false);
    });

    return () => {
      if (socket.current) {
        socket.current.disconnect();
      }
    };
  }, []);

  return (
    <SocketContext.Provider
      value={{
        socket: socket.current,
        isConnected: socketIsConnected,
        notifications,
        // addNotification,
        unreadNotifications,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export default SocketProvider;
